import * as React from 'react';

interface AudioPlayerContextValue {
  /** Start `url` under `key`, or pause it when it is already the playing clip. */
  toggle: (key: string, url: string) => void;
  stop: () => void;
  isActive: (key: string) => boolean;
  isPlaying: (key: string) => boolean;
  isLoading: (key: string) => boolean;
  /** True once the clip has been played all the way through in this session. */
  hasListened: (key: string) => boolean;
  error: string | null;
}

const AudioPlayerContext = React.createContext<AudioPlayerContextValue | null>(null);

/**
 * SPEC §11 — ONE shared player globally: starting any clip stops the previous one,
 * and a URL is only loaded on first play.
 */
export function AudioPlayerProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const activeKeyRef = React.useRef<string | null>(null);
  const [activeKey, setActiveKey] = React.useState<string | null>(null);
  const [playing, setPlaying] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [listened, setListened] = React.useState<ReadonlySet<string>>(() => new Set());
  const [error, setError] = React.useState<string | null>(null);

  const getAudio = React.useCallback((): HTMLAudioElement => {
    if (!audioRef.current) audioRef.current = new Audio();
    return audioRef.current;
  }, []);

  React.useEffect(() => {
    const audio = getAudio();
    const onEnded = (): void => {
      setPlaying(false);
      // SPEC §11 — the "listened" dot only appears after a full playthrough.
      const finished = activeKeyRef.current;
      if (finished) setListened((previous) => new Set(previous).add(finished));
    };
    const onPlaying = (): void => {
      setLoading(false);
      setPlaying(true);
    };
    const onPause = (): void => setPlaying(false);
    const onError = (): void => {
      setLoading(false);
      setPlaying(false);
      setError('Could not play this clip');
    };
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('error', onError);
      audio.pause();
    };
  }, [getAudio]);

  const value = React.useMemo<AudioPlayerContextValue>(() => {
    const start = (key: string, url: string): void => {
      const audio = getAudio();
      setError(null);
      if (audio.getAttribute('src') !== url) {
        // Lazy load: the object is only fetched the first time it is played.
        audio.setAttribute('src', url);
        audio.load();
      }
      activeKeyRef.current = key;
      setActiveKey(key);
      setLoading(true);
      void Promise.resolve(audio.play())
        .then(() => {
          setLoading(false);
          setPlaying(true);
        })
        .catch(() => {
          setLoading(false);
          setPlaying(false);
          setError('Could not play this clip');
        });
    };

    return {
      toggle: (key, url) => {
        const audio = getAudio();
        if (activeKey === key && playing) {
          audio.pause();
          setPlaying(false);
          return;
        }
        if (activeKey !== null && activeKey !== key) audio.pause();
        start(key, url);
      },
      stop: () => {
        getAudio().pause();
        setPlaying(false);
      },
      isActive: (key) => activeKey === key,
      isPlaying: (key) => activeKey === key && playing,
      isLoading: (key) => activeKey === key && loading,
      hasListened: (key) => listened.has(key),
      error,
    };
  }, [activeKey, playing, loading, listened, error, getAudio]);

  return <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>;
}

export function useAudioPlayer(): AudioPlayerContextValue {
  const context = React.useContext(AudioPlayerContext);
  if (!context) throw new Error('useAudioPlayer must be used inside <AudioPlayerProvider>');
  return context;
}
